import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, CardTitle, CardBody, Button } from "reactstrap";
import axios from "../../api/Axios";

const ProviderDetailsPage = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activating, setActivating] = useState(false);

  const activateAccount = async () => {
    if (!provider || activating) return;
    
    try {
      setActivating(true);
      
      // Call backend to activate account
      await axios.post(`/accounts/providers/${provider.id}/activate/`, {
        is_activated: true,
        reg_status: 2
      });
      
      // Update local provider state
      setProvider(prev => ({
        ...prev,
        is_activated: true,
        reg_status: 2
      }));
      
      alert('Account activated successfully!');
      
    } catch (error) {
      console.error('Error activating account:', error);
      alert('Failed to activate account. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    const fetchProviderDetails = async () => {
      try {
        setLoading(true);
        const res = await axios.get("/accounts/providers-with-docs/");
        const foundProvider = res.data.find(p => p.id === parseInt(providerId));
        
        if (foundProvider) {
          setProvider(foundProvider);
        } else {
          setError('Provider not found');
        }
      } catch (err) {
        console.error('Error fetching provider details:', err);
        setError('Failed to fetch provider details');
      } finally {
        setLoading(false);
      }
    };

    if (providerId) {
      fetchProviderDetails();
    }
  }, [providerId]);

  const getProviderType = (provider) => {
    if (!provider) return 'N/A';
    if (provider.provider_type === 'individual') return 'Individual';
    if (provider.provider_type === 'entity') return 'Business/Entity';
    return 'Legacy Provider';
  };

  const getProviderDisplayName = (provider) => {
    if (!provider) return 'N/A';
    
    if (provider.provider_type === 'entity' && provider.company_name) {
      return provider.company_name;
    }
    
    const firstName = provider.first_name || '';
    const lastName = provider.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'N/A';
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading provider details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Card className="mt-3">
          <CardBody className="text-center">
            <h5 className="text-danger">Error</h5>
            <p>{error}</p>
            <Button color="primary" onClick={() => navigate('/backoffice-dashboard/ManageProviders')}>
              Back to Providers
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="container-fluid">
        <Card className="mt-3">
          <CardBody className="text-center">
            <h5>Provider Not Found</h5>
            <p>The requested provider could not be found.</p>
            <Button color="primary" onClick={() => navigate('/backoffice-dashboard/ManageProviders')}>
              Back to Providers
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-building me-2"></i>
                Provider Details
              </h2>
              <p className="text-muted mb-0">
                Detailed information for {getProviderDisplayName(provider)}
              </p>
            </div>
            <div className="d-flex gap-2">
              {/* Activate Account Button - Show only if account is not activated */}
              {!provider.is_activated && (
                <Button 
                  color="success" 
                  onClick={activateAccount}
                  disabled={activating}
                >
                  {activating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Activating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Activate Account
                    </>
                  )}
                </Button>
              )}
              
              <Button 
                color="secondary" 
                onClick={() => navigate('/backoffice-dashboard/ManageProviders')}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Providers
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        {/* Provider Information */}
        <Col lg="8">
          <Card className="mb-4">
            <CardTitle tag="h5" className="border-bottom p-3 mb-0">
              <i className="bi bi-person-circle me-2"></i>
              Provider Information
            </CardTitle>
            <CardBody>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <strong>Provider Type:</strong>
                    <div className="mt-1">{getProviderType(provider)}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong>
                    <div className="mt-1">{provider.email || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Phone:</strong>
                    <div className="mt-1">{provider.contact_phone || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>ID Type:</strong>
                    <div className="mt-1">{provider.id_type || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>ID Number:</strong>
                    <div className="mt-1">{provider.id_no || 'N/A'}</div>
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <strong>Title:</strong>
                    <div className="mt-1">{provider.title || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Registration Status:</strong>
                    <div className="mt-1">
                      <span className={`badge ${
                        provider.reg_status === 0 ? 'bg-warning' :
                        provider.reg_status === 1 ? 'bg-info' :
                        provider.reg_status === 2 ? 'bg-success' : 'bg-secondary'
                      }`}>
                        {provider.reg_status === 0 ? 'Incomplete' :
                         provider.reg_status === 1 ? 'Under Review' :
                         provider.reg_status === 2 ? 'Complete' : 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Email Verified:</strong>
                    <div className="mt-1">
                      <span className={`badge ${provider.email_verified ? 'bg-success' : 'bg-danger'}`}>
                        {provider.email_verified ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Account Activated:</strong>
                    <div className="mt-1">
                      <span className={`badge ${provider.is_activated ? 'bg-success' : 'bg-warning'}`}>
                        {provider.is_activated ? 'Active' : 'Inactive'}
                      </span>
                      {provider.is_activated && (
                        <div className="mt-1">
                          <small className="text-success">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            Account is fully activated and can access all features
                          </small>
                        </div>
                      )}
                      {!provider.is_activated && (
                        <div className="mt-1">
                          <small className="text-warning">
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            Account needs activation to access provider features
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Entity-specific information */}
              {provider.provider_type === 'entity' && (
                <div className="mt-4 pt-3 border-top">
                  <h6 className="mb-3">
                    <i className="bi bi-building me-2"></i>
                    Business Information
                  </h6>
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <strong>Company Name:</strong>
                        <div className="mt-1">{provider.company_name || 'N/A'}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Registration Number:</strong>
                        <div className="mt-1">{provider.registration_number || 'N/A'}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Business Phone:</strong>
                        <div className="mt-1">{provider.business_phone || 'N/A'}</div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <strong>Business Email:</strong>
                        <div className="mt-1">{provider.business_email || 'N/A'}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Representative Position:</strong>
                        <div className="mt-1">{provider.representative_position || 'N/A'}</div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Individual-specific information */}
              {provider.provider_type === 'individual' && provider.business_description && (
                <div className="mt-4 pt-3 border-top">
                  <h6 className="mb-3">
                    <i className="bi bi-briefcase me-2"></i>
                    Business Description
                  </h6>
                  <div className="p-3 bg-light rounded">
                    {provider.business_description}
                  </div>
                </div>
              )}

              {/* Address information */}
              {(provider.personal_address || provider.business_address) && (
                <div className="mt-4 pt-3 border-top">
                  <h6 className="mb-3">
                    <i className="bi bi-geo-alt me-2"></i>
                    Address Information
                  </h6>
                  <div className="p-3 bg-light rounded">
                    {provider.provider_type === 'entity' ? 
                      (provider.business_address || provider.personal_address) :
                      provider.personal_address
                    }
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Documents Panel */}
        <Col lg="4">
          <Card>
            <CardTitle tag="h5" className="border-bottom p-3 mb-0">
              <i className="bi bi-file-earmark-text me-2"></i>
              Documents
            </CardTitle>
            <CardBody>
              {provider.documents && provider.documents.length > 0 ? (
                <div>
                  {provider.documents.map((doc, index) => (
                    <div key={index} className="mb-3 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{doc.doc_type}</h6>
                          <small className="text-muted">
                            Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                          </small>
                        </div>
                        <span className={`badge ${
                          doc.doc_status === 'Approved' || doc.doc_status === 'Accepted' ? 'bg-success' :
                          doc.doc_status === 'Rejected' ? 'bg-danger' :
                          'bg-warning'
                        }`}>
                          {doc.doc_status}
                        </span>
                      </div>
                      {doc.comments && (
                        <div className="mt-2">
                          <small><strong>Comments:</strong> {doc.comments}</small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted">
                  <i className="bi bi-file-earmark display-4"></i>
                  <p className="mt-2">No documents uploaded</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderDetailsPage;